import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Avatar,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useDebounce } from '../../hooks/useDebounce';
import restrictionAdminService from '../../services/restrictionAdminService';

/**
 * Composant de recherche et sélection d'utilisateur
 * 
 * @param {Object} props
 * @param {string} props.value - ID de l'utilisateur sélectionné
 * @param {Function} props.onChange - Callback appelé avec l'ID de l'utilisateur sélectionné
 * @param {string} props.label - Label du champ
 * @param {string} props.placeholder - Placeholder du champ
 * @param {boolean} props.required - Champ obligatoire
 * @param {string} props.error - Message d'erreur
 * @param {boolean} props.disabled - Champ désactivé
 */
const UserSearchField = ({
  value,
  onChange,
  label = 'Utilisateur',
  placeholder = 'Rechercher par nom ou email...',
  required = false,
  error = null,
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const debouncedInputValue = useDebounce(inputValue, 300);

  // Recherche des utilisateurs
  useEffect(() => {
    if (!debouncedInputValue || debouncedInputValue.length < 2) {
      setOptions([]);
      setSearchError(null);
      return;
    }

    const searchUsers = async () => {
      setLoading(true);
      setSearchError(null);
      try {
        console.log('Searching users with query:', debouncedInputValue);
        const response = await restrictionAdminService.searchUsers(debouncedInputValue);
        console.log('Search response:', response);
        // La réponse est {users: [...]} et non {data: [...]}
        const usersList = response.users || [];
        setOptions(usersList);
        if (usersList.length === 0) {
          console.log('No users found for query:', debouncedInputValue);
        }
      } catch (err) {
        console.error('Error searching users:', err);
        setSearchError(err.response?.data?.error || err.message);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    searchUsers();
  }, [debouncedInputValue]);

  // Chargement initial si une valeur est fournie
  useEffect(() => {
    if (value && !selectedUser) {
      // Si on a un ID mais pas l'objet utilisateur complet, 
      // on pourrait faire une recherche pour récupérer les infos
      // Pour l'instant on affiche juste l'ID
      setSelectedUser({ id: value, email: value, name: 'Chargement...' });
    }
  }, [value, selectedUser]);

  const handleChange = (event, newValue) => {
    setSelectedUser(newValue);
    onChange(newValue ? newValue.id : '');
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedUser}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(option) => option.email || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x} // Désactive le filtre client (on filtre côté serveur)
      noOptionsText={
        inputValue.length < 2
          ? 'Entrez au moins 2 caractères'
          : searchError
          ? `Erreur: ${searchError}`
          : 'Aucun utilisateur trouvé'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error || !!searchError}
          helperText={error || searchError || 'Recherchez par nom ou email (min. 2 caractères)'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            <PersonIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.email}
            </Typography>
          </Box>
          {option.role === 'admin' && (
            <Chip label="Admin" size="small" color="primary" />
          )}
        </Box>
      )}
    />
  );
};

export default UserSearchField;
